// 면접 스케줄 공통 상수/유틸. 근무·평가 항목은 서버 router/interview.tsx 화이트리스트와 같아야 함.
import totalLocationData from '../../../data/locationdata';

export const JOBS = [
    "미정", "야간사감", "주간사감", "오전데스크", "오후데스크", "저녁데스크",
    "공부병행 오후데스크", "공부병행 저녁데스크", "평일당직", "평일오전당직",
    "토야간당직", "일주간당직", "일야간당직", "주말당직",
];

export const EVAL_LISTS: { [key: string]: string[] } = {
    "당직": ["장기 근무 가능성", "근태 성실성 및 책임감", "전화하는 상대방에게 긍정적인 느낌을 받을 수 있는 말투", "변기뚫기 가능한지 (P/F)"],
    "사감": ["학생들이 편안함을 느끼고 잘 따를 수 있는 인상 및 말투", "근태 성실성 및 책임감", "장기 근무 가능성", "학생 관리 능력 (자습 분위기 유지, 생활 지도, 세심한 관리 등)", "변기뚫기 가능한지 (P/F)"],
    "데스크": ["학부모 상담 시 신뢰감 있고 원활한 소통이 가능한 말투 및 태도", "학생들에게 친절하고 긍정적인 인상을 줄 수 있는 태도와 말투", "결제 안내, 벌점 관리, 신규 OT 및 상담 진행 등 업무 처리 능력 및 이해도", "근태 성실성 및 책임감", "장기 근무 가능성", "변기뚫기 가능한지 (P/F)"],
};

export const DOW = ["월", "화", "수", "목", "금", "토", "일"];

// 평가자별 평가 (서버 interview_evaluation)
export interface InterviewEvaluation {
    evaluatorId: number;
    evaluatorName: string;
    grades: string[];
    memo: string;
}

export interface InterviewItem {
    id: number;
    date: string;            // YYYY-MM-DD
    time: string;            // HH:MM
    location: string;        // 한글 지점명 (신촌점)
    job: string;
    name: string;
    phone: string | null;
    status: number;          // 0 예정 / 1 완료 / 2 노쇼 (면접자 공통)
    category: string | null; // null이면 근무명으로 자동 (면접자 공통)
    evaluations: InterviewEvaluation[]; // 먼저 평가한 사람이 앞
}

// 등급이 하나라도 찍힌 평가만 칩·리스트에 표시
export const hasGrades = (ev: InterviewEvaluation) => !!ev.grades && ev.grades.some((g) => g);
// ["C","","B"] → "C - B"
export const gradeStr = (ev: InterviewEvaluation) => ev.grades.map((g) => g || "-").join(" ");
// "심윤주 부원장" → "심윤주"
export const short3 = (name: string) => (name || "").replace(/\s+/g, "").slice(0, 3);

export interface InterviewPrefill {
    name: string;
    location: string;
    job: string;
    phone: string;
}

// 근무명 → 평가 리스트 종류
export const catOf = (job: string) => job.indexOf("사감") >= 0 ? "사감" : job.indexOf("데스크") >= 0 ? "데스크" : job.indexOf("당직") >= 0 ? "당직" : "";
export const categoryOf = (item: InterviewItem) => item.category !== null && item.category !== undefined ? item.category : catOf(item.job);

// sunbae 지점 (한글값)
export const sunbaeLocations: string[] = totalLocationData
    .filter((l) => l.academy.includes("sunbae") && l.english !== "total")
    .map((l) => l.korean);

// "신촌점" → "신촌" (칩·헤더 표시용)
export const shortLocation = (loc: string) => loc.endsWith("점") ? loc.slice(0, -1) : loc;

const pad = (n: number) => String(n).padStart(2, "0");
export const dateKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const parseKey = (k: string) => new Date(+k.slice(0, 4), +k.slice(5, 7) - 1, +k.slice(8, 10));
// 월요일 기준 요일 index (0=월)
export const dowIndex = (d: Date) => (d.getDay() + 6) % 7;
export const mondayOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate() - dowIndex(d));

// "15:30" → "오후 3시 30분"
export const fmtTime = (t: string) => {
    const h = +t.slice(0, 2), m = t.slice(3, 5);
    const s = (h < 12 ? "오전 " : "오후 ") + (h % 12 || 12) + "시";
    return m === "00" ? s : s + " " + (+m) + "분";
};

// "10/15 (목) 오후 3시"
export const fmtWhen = (item: InterviewItem) => {
    const d = parseKey(item.date);
    return `${d.getMonth() + 1}/${d.getDate()} (${DOW[dowIndex(d)]}) ${fmtTime(item.time)}`;
};

// "10/20 (화)" (리스트 날짜 열)
export const fmtListDate = (k: string) => {
    const d = parseKey(k);
    return `${d.getMonth() + 1}/${pad(d.getDate())} (${DOW[dowIndex(d)]})`;
};

// 문자방 연락처 설명("하남점 주간사감//당근", "신촌 토야간당직//당근")에서 지점·근무 추출. 가장 긴 매칭 우선.
export const parseRoomDefaults = (name: string, description: string, phone: string): InterviewPrefill => {
    const text = ((description || "").split("//")[0] || "").trim();
    let location = "";
    sunbaeLocations.forEach((loc) => {
        const short = shortLocation(loc);
        if (text.indexOf(short) >= 0 && short.length > shortLocation(location).length) location = loc;
    });
    let job = "미정";
    JOBS.forEach((j) => {
        if (j !== "미정" && text.indexOf(j) >= 0 && (job === "미정" || j.length > job.length)) job = j;
    });
    return { name: name || "", location, job, phone: phone || "" };
};
